import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import ReactPaginate from "react-paginate";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import Header from "../../components/header/header";
import { approvePaidUpCapitalAction, getPaidUpCapitalAction } from "../../actions/payment";
import { getBusinessAction, resetBusinessList } from "../../actions/businessInfo";
import { SELECT_BOX_THEME, styleSelect } from "../../constants";
import debounce from "lodash/debounce";
import Confirmmodel from "../../components/addBussiness/Confirmmodel";

const SHARE_TYPE_NAME = {
  1: "Ordinary",
  2: "Preference",
};

export default function PaidUp() {
  const dispatch = useDispatch();
  const { control } = useForm();
  const [page, setPage] = useState(1);
  const [activePage, setActivePage] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const [confirmShow, setConfirmShow] = useState(false);
  const [selectedCapital, setSelectedCapital] = useState(null);
  const [isApproving, setIsApproving] = useState(false);
  const isApprovingRef = useRef(false);
  const limit = 20;
  const companyName = useWatch({ control, name: "companyName" });
  const { paidUpCapitalList, paidUpCapitalCount } = useSelector((state) => state.payment);
  const { businessInfo } = useSelector((state) => state.businessInfo);

  useEffect(() => {
    dispatch(getPaidUpCapitalAction(page, limit, companyName?._id || ""));
  }, [page, companyName, dispatch]);

  useEffect(() => {
    const debouncedSearch = debounce((value) => {
      if (value) {
        dispatch(getBusinessAction(1, 100, value, "", "", "", "", "", "", "", "", ""));
      } else {
        dispatch(resetBusinessList());
      }
    }, 1000);

    debouncedSearch(searchValue);
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchValue, dispatch]);

  const handleCompanyChange = (field, value) => {
    field.onChange(value);
    setPage(1);
    setActivePage(0);
  };

  const handlePageClick = (e) => {
    const selectedPage = e.selected;
    setActivePage(selectedPage);
    setPage(selectedPage + 1);
  };

  const handleApprove = (capital) => {
    setSelectedCapital(capital);
    setConfirmShow(true);
  };

  const closeConfirmModal = () => {
    setConfirmShow(false);
    setSelectedCapital(null);
  };

  const confirmApprove = () => {
    if (isApprovingRef.current) {
      return;
    }

    if (!selectedCapital) {
      closeConfirmModal();
      return;
    }

    isApprovingRef.current = true;
    setIsApproving(true);
    dispatch(approvePaidUpCapitalAction(selectedCapital, {
      page,
      limit,
      companyId: companyName?._id || "",
    })).then(() => {
      closeConfirmModal();
    }).finally(() => {
      isApprovingRef.current = false;
      setIsApproving(false);
    });
  };

  const handleChange = (inputValue) => {
    setSearchValue(inputValue);
  };

  const formatOptionLabel = ({ companyName, isAdvisor }) => {
    return (
      <div className="menuController">
        <p className="name">{companyName}</p>
        {isAdvisor && <p>(advisor)</p>}
      </div>
    );
  };

  const getShareCapital = (item) => {
    return `${item?.currency || ""} ${item?.shareCapital ?? "-"}`.trim();
  };

  const getShareType = (shareType) => {
    return SHARE_TYPE_NAME[shareType] || "-";
  };

  const getCompanyName = (item) => {
    return item?.company?.companyName || "-";
  };

  const getBalance = (item) => {
    return Number(item?.shareCapital || 0) - Number(item?.paidAmount || 0);
  };

  const isApproveDisabled = (item) => {
    return getBalance(item) <= 0.000001;
  };

  return (
    <>
      <Header title="Paid Up Capital" />
      <div className="container-fluid py-4">
        <div className="row">
          <div className="col-lg-12 col-xl-12 col-md-12 col-sm-12 mb-2">
            <div className="card planList notification h-100">
              <div className="card-headings">
                <h6 className="font-weight-bolder payment-text mb-0">
                  Paid Up Capital List
                </h6>
              </div>
              <div className="searchbar m-0">
                <Controller
                  name="companyName"
                  control={control}
                  render={({ field }) => (
                    <Select
                      className="select-border"
                      theme={SELECT_BOX_THEME}
                      {...field}
                      styles={styleSelect}
                      isClearable={true}
                      isSearchable={true}
                      options={businessInfo}
                      getOptionLabel={(option) => option?.companyName}
                      formatOptionLabel={formatOptionLabel}
                      onChange={(value) => handleCompanyChange(field, value)}
                      onInputChange={handleChange}
                      getOptionValue={(option) => option._id}
                      placeholder="Search business name"
                      components={{
                        DropdownIndicator: () => null,
                        IndicatorSeparator: () => null,
                      }}
                    />
                  )}
                />
                <img src="assets/img/searchIcon.svg" alt="" />
              </div>
              <div className="card-body detail">
                <table className="businessTable userTable">
                  <thead>
                    <tr>
                      <td>Share Capital</td>
                      <td>Share Type</td>
                      <td>Shares</td>
                      <td>Company Name</td>
                      <td>Action</td>
                    </tr>
                  </thead>
                  <tbody>
                    {paidUpCapitalList.length > 0 ? (
                      paidUpCapitalList.map((item) => (
                        <tr key={item.id}>
                          <td>
                            <h6 className="profile-text text-sm">
                              {getShareCapital(item)}
                            </h6>
                          </td>
                          <td>
                            <h6 className="profile-text profile-text-fromuser text-sm">
                              {getShareType(item.shareType)}
                            </h6>
                          </td>
                          <td>
                            <h6 className="profile-text profile-text-fromuser text-sm">
                              {item.shares}
                            </h6>
                          </td>
                          <td>
                            <h6 className="profile-text profile-text-fromuser text-sm">
                              {getCompanyName(item)}
                            </h6>
                          </td>
                          <td>
                            <button
                              className="btn save-btn editButton"
                              disabled={isApproveDisabled(item)}
                              onClick={() => handleApprove(item.id)}
                            >
                              {isApproveDisabled(item) ? "Approved" : "Approve"}
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5">
                          <h6 className="mb-0 text-sm table-heading text-center">
                            No paid up capital found
                          </h6>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            {paidUpCapitalCount > 1 && (
              <div className="pagination-section">
                <ReactPaginate
                  previousLabel={"Prev"}
                  nextLabel={"Next"}
                  breakLabel={"..."}
                  breakClassName={"break-me"}
                  pageCount={paidUpCapitalCount}
                  marginPagesDisplayed={1}
                  pageRangeDisplayed={3}
                  onPageChange={handlePageClick}
                  containerClassName={"pagination"}
                  subContainerClassName={"pages pagination"}
                  activeClassName={"active"}
                  forcePage={activePage}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <Confirmmodel
        message="Are you sure you want to approve this paid up capital?"
        addModalShow={confirmShow}
        addModalClose={closeConfirmModal}
        confirmModal={confirmApprove}
        confirmDisabled={isApproving}
        confirmText={isApproving ? "Approving..." : "Confirm"}
      />
    </>
  );
}
